import React, { useEffect, useRef } from "react";
import { PixelFormValues } from "./CreatePixelForm";
import { DialogStateProvider } from "./context/DialogStateContext";
import { PixelDialogContentWrapper } from "./components/PixelDialogContentWrapper";
import { CreatePixelContent } from "./components/CreatePixelContent";
import { InstallationContent } from "./components/InstallationContent";

interface PixelDialogContentProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pixelId: string | null;
  isSubmitting: boolean;
  onSubmit: (data: PixelFormValues) => Promise<void>;
  onTestPixel: () => Promise<void>;
  onAllowClose?: () => void;
}

export function PixelDialogContent({ 
  open, 
  onOpenChange, 
  pixelId, 
  isSubmitting, 
  onSubmit,
  onTestPixel,
  onAllowClose
}: PixelDialogContentProps) {
  const pixelIdRef = useRef<string | null>(null);
  const isInstallationPhase = Boolean(pixelId);
  const prevOpenRef = useRef(open);
  const mountedRef = useRef(false);
  const stableOnChangeRef = useRef(onOpenChange);
  const transitionInProgressRef = useRef(false);
  const currentViewRef = useRef<'create' | 'installation'>(isInstallationPhase ? 'installation' : 'create');

  // Update ref to always have the latest onOpenChange function
  useEffect(() => {
    stableOnChangeRef.current = onOpenChange;
  }, [onOpenChange]);

  // Track pixelId changes for debugging
  useEffect(() => {
    const oldPixelId = pixelIdRef.current;
    pixelIdRef.current = pixelId;
    
    // Update current view reference to prevent flashing
    if (pixelId) {
      currentViewRef.current = 'installation';
    } else if (!isSubmitting) {
      currentViewRef.current = 'create';
    }
    
    // If pixelId is set while dialog is open, ensure we keep it open
    if (pixelId && open) {
      // Force dialog to stay open when pixel ID is set
      stableOnChangeRef.current(true);
    }
  }, [pixelId, isInstallationPhase, open, isSubmitting]);

  // Set transition flag when opening/closing to prevent flashing
  useEffect(() => {
    if (prevOpenRef.current !== open) {
      transitionInProgressRef.current = true;
      // Reset transition flag after animation completes
      setTimeout(() => {
        transitionInProgressRef.current = false;
      }, 300); // Match dialog animation duration
    }
    
    prevOpenRef.current = open;
  }, [open, pixelId, isSubmitting, isInstallationPhase]);

  // On component mount/unmount
  useEffect(() => {
    mountedRef.current = true;
    
    // Ensure dialog is open on mount if it should be
    if ((isSubmitting || isInstallationPhase) && !open) {
      stableOnChangeRef.current(true);
    }
    
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Make sure dialog stays open when needed
  useEffect(() => {
    if (!open && isSubmitting) {
      stableOnChangeRef.current(true);
    }
  }, [open, isSubmitting]);

  const handleCloseClick = () => {
    // Set transition flag to prevent content flashing during close
    transitionInProgressRef.current = true;
    
    if (onAllowClose) {
      onAllowClose();
    } else {
      // Fallback if no specific handler provided
      stableOnChangeRef.current(false);
    }
  };

  // Determine which content to show based on state and transition status
  const renderDialogContent = () => {
    // During transitions, maintain the current view to prevent flashing
    if (transitionInProgressRef.current) {
      return currentViewRef.current === 'installation' ? (
        <InstallationContent 
          pixelId={pixelId || ''} 
          onTestPixel={onTestPixel} 
          onClose={handleCloseClick} 
        />
      ) : (
        <CreatePixelContent 
          onSubmit={onSubmit} 
          isSubmitting={isSubmitting} 
        />
      );
    }
    
    // Regular rendering when not in transition
    return !pixelId ? (
      <CreatePixelContent 
        onSubmit={onSubmit} 
        isSubmitting={isSubmitting} 
      />
    ) : (
      <InstallationContent 
        pixelId={pixelId} 
        onTestPixel={onTestPixel} 
        onClose={handleCloseClick} 
      />
    );
  };

  return (
    <DialogStateProvider
      open={open}
      onOpenChange={onOpenChange}
      isSubmitting={isSubmitting}
      forceKeepOpen={false}
      installationPhase={isInstallationPhase}
    >
      <PixelDialogContentWrapper>
        {renderDialogContent()}
      </PixelDialogContentWrapper>
    </DialogStateProvider>
  );
}
